import { DateToIsoString, NullableDateToIsoString } from '@mntn/api-contracts/_shared/date-to-iso-string'
import {
  ImageAttachmentFormSchema,
  ImageAttachmentFormSchemaOptionalCrop,
  ImageAttachmentResponseWithCroppedUrlSchema,
} from '@mntn/api-contracts/_shared/image-attachments'
import { PageResponseSchema } from '@mntn/api-contracts/_shared/page-response'
import { PaginationQuerySchema } from '@mntn/api-contracts/_shared/pagination'
import { ProgramGenreResponseSchema } from '@mntn/api-contracts/seller/v1/program-genres'
import { ProgramRatingResponseSchema } from '@mntn/api-contracts/seller/v1/program-ratings'
import { ProgramSchema } from '@mntn/api-contracts/seller/v1/programs'
import { VideoAttachmentSchema, VideoMimeTypeSchema } from '@mntn/api-contracts/seller/v1/video-attachments'
import {
  Actors as A,
  authorizeAdvertiserIdBodyTransform,
  createAuthorizeAdvertiserIdTransform,
  Tenants as T,
} from '@mntn/auth-types'
import { ErrorSchema as E, ErrorResponseSchema } from '@mntn/result'
import {
  CoerceBooleanSchema,
  CoerceNumberSchema,
  IDSchema,
  NaturalNumberSchema,
  PositiveDecimalSchema,
  QueryIDsSchema,
  RequiredCoerceDateSchema,
  WholeNumberSchema,
} from '@mntn/zod'
import { z } from 'zod'
import { SalesSheetSchema } from './sales-sheet.ts'

const POSTGRES_INTEGER_MAX = 2147483647

/**
 * Campaign Template Group IDs are default assigned to every offering version and are utilized when creating campaigns from Flight Deck.
 * These template group IDs correspond to campaign templates that are optimized to improve bidding for specific types of programs available in MNTN Select.
 * This ensures that campaigns are configured for optimal performance according to the nature of the selected program type.
 */
export const CampaignTemplateGroupIds = {
  MNTN_SELECT: 7,
  LIVE_SPORTS: 8,
} as const

type MakePartialSchema<TSchema extends z.ZodRawShape> = {
  [K in keyof TSchema]: z.ZodOptional<TSchema[K]>
}

const isPartialSchema = <TSchema extends z.ZodRawShape>(
  schema: z.ZodObject<TSchema> | z.ZodObject<MakePartialSchema<TSchema>>,
): schema is z.ZodObject<MakePartialSchema<TSchema>> =>
  Object.values(schema.shape).some(field => field instanceof z.ZodOptional)

type ValidationFor<TShape extends z.ZodRawShape> = Array<
  (data: z.infer<z.ZodObject<TShape>>, ctx: z.RefinementCtx) => void | Promise<void>
>

function applyValidations<TSchema extends z.ZodRawShape>(
  schema: z.ZodObject<TSchema>,
  validations: ValidationFor<TSchema>,
): z.ZodObject<TSchema>
function applyValidations<TSchema extends z.ZodRawShape>(
  schema: z.ZodObject<MakePartialSchema<TSchema>>,
  validations: ValidationFor<MakePartialSchema<TSchema>>,
): z.ZodObject<MakePartialSchema<TSchema>>
function applyValidations<TSchema extends z.ZodRawShape>(
  schema: z.ZodObject<TSchema> | z.ZodObject<MakePartialSchema<TSchema>>,
  validations: ValidationFor<TSchema> | ValidationFor<MakePartialSchema<TSchema>>,
): z.ZodObject<TSchema> | z.ZodObject<MakePartialSchema<TSchema>> {
  if (isPartialSchema(schema)) {
    // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
    const partialSchema = schema as z.ZodObject<MakePartialSchema<TSchema>>
    // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
    const partialValidations = validations as ValidationFor<MakePartialSchema<TSchema>>
    return partialSchema.superRefine((data, ctx) => {
      for (const validator of partialValidations) {
        validator(data, ctx)
      }
    })
  }

  // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
  const fullSchema = schema as z.ZodObject<TSchema>
  // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
  const fullValidations = validations as ValidationFor<TSchema>
  return fullSchema.superRefine((data, ctx) => {
    for (const validator of fullValidations) {
      validator(data, ctx)
    }
  })
}

type PartialSchemaSuperRefinement<T extends z.ZodObject> = (
  data: Partial<z.output<T>>,
  ctx: z.RefinementCtx,
) => void | Promise<void>

/**
 * Validations
 */

const availabilityEndAfterStart = (
  {
    availabilityStartTime,
    availabilityEndTime,
  }: Partial<Pick<z.output<typeof OfferingInputSchema>, 'availabilityStartTime' | 'availabilityEndTime'>>,
  ctx: z.RefinementCtx,
) => {
  if (!availabilityStartTime || !availabilityEndTime) return

  if (availabilityEndTime <= availabilityStartTime) {
    ctx.addIssue({
      path: ['availabilityEndTime'],
      message: 'Deal availability end must be after availability start',
      code: 'custom',
      params: { availabilityStartTime, availabilityEndTime },
    })
  }
}

const runEndAfterStart = (
  { runStartTime, runEndTime }: Partial<Pick<z.output<typeof OfferingInputSchema>, 'runStartTime' | 'runEndTime'>>,
  ctx: z.RefinementCtx,
) => {
  if (!runStartTime || !runEndTime) return

  if (runEndTime <= runStartTime) {
    ctx.addIssue({
      path: ['runEndTime'],
      message: 'Deal air time end must be after air time start',
      code: 'custom',
      params: { runStartTime, runEndTime },
    })
  }
}

const impressionsGreaterThanImpressionsSold: PartialSchemaSuperRefinement<typeof OfferingValidationSchema> = (
  { impressionsSold, impressions },
  ctx,
) => {
  if (impressionsSold === undefined || impressions === undefined || impressions === null) return

  if (impressions < impressionsSold) {
    ctx.addIssue({
      path: ['impressions'],
      message: 'Impressions cannot be lower than amount sold',
      code: 'custom',
      params: { impressionsSold, impressions },
    })
  }
}

const featuredFieldsRefinement = (
  {
    isFeatured,
    featuredOverline,
    featuredTagline,
  }: Partial<Pick<z.output<typeof OfferingInputSchema>, 'isFeatured' | 'featuredOverline' | 'featuredTagline'>>,
  ctx: z.RefinementCtx,
) => {
  if (isFeatured) {
    if (!featuredOverline || featuredOverline.length === 0) {
      ctx.addIssue({
        path: ['featuredOverline'],
        message: 'Overline is required when deal is featured',
        code: 'custom',
        params: { isFeatured, featuredOverline },
      })
    }
    if (!featuredTagline || featuredTagline.length === 0) {
      ctx.addIssue({
        path: ['featuredTagline'],
        message: 'Tagline is required when deal is featured',
        code: 'custom',
        params: { isFeatured, featuredTagline },
      })
    }
    // Image validation is handled at the PostOfferingBodySchema level via featuredImageAttachmentsRefinement
  }
}

const featuredImageAttachmentsRefinement = (
  data: {
    offering?: { isFeatured?: boolean }
    imageAttachments?: { featured?: { url?: string } }
  },
  ctx: z.RefinementCtx,
) => {
  if (data.offering?.isFeatured) {
    const url = data.imageAttachments?.featured?.url
    const hasFeaturedImage = url !== undefined && url.length > 0
    if (!hasFeaturedImage) {
      ctx.addIssue({
        path: ['imageAttachments', 'featured'],
        message: 'Image is required when deal is featured',
        code: 'custom',
        params: { isFeatured: data.offering.isFeatured },
      })
    }
  }
}

export const OFFERING_VALIDATIONS: Array<PartialSchemaSuperRefinement<typeof OfferingInputSchema>> = [
  availabilityEndAfterStart,
  runEndAfterStart,
  impressionsGreaterThanImpressionsSold,
  featuredFieldsRefinement,
]

export const OFFERING_VERSION_VALIDATIONS: Array<PartialSchemaSuperRefinement<typeof OfferingValidationSchema>> = [
  availabilityEndAfterStart,
  runEndAfterStart,
  impressionsGreaterThanImpressionsSold,
  featuredFieldsRefinement,
]

/**
 * Schemas
 */

const DemographicsSummarySchema = z
  .string()
  .max(30, 'Summary must be 30 characters or less')
  .regex(/^[A-Z]/, 'Summary must start with a capital letter')
  .refine(summary => !/[.,;:/&-]\s*$/.test(summary.trim()), {
    message: 'Summary must not end with unfinished punctuation',
  })

const DemographicsSummaryReasonSchema = z.string().max(240, 'Summary reason must be 240 characters or less')

export const DemographicsSchema = z
  .object({
    ageRange: z.string().min(1, 'Age range is required').max(25, 'Age range must be 25 characters or less'),
    malePercentage: z
      .number()
      .min(0, 'Male percentage must be at least 0')
      .max(100, 'Male percentage cannot exceed 100'),
    femalePercentage: z
      .number()
      .min(0, 'Female percentage must be at least 0')
      .max(100, 'Female percentage cannot exceed 100'),
    sellLine: z.string().min(1, 'Sell line is required').max(200, 'Sell line must be 200 characters or less'),
    mindsetSummary: DemographicsSummarySchema,
    engagementSummary: DemographicsSummarySchema,
    overIndexSummary: DemographicsSummarySchema,
    valueSummary: DemographicsSummarySchema,
    mindsetSummaryReason: DemographicsSummaryReasonSchema.optional(),
    engagementSummaryReason: DemographicsSummaryReasonSchema.optional(),
    overIndexSummaryReason: DemographicsSummaryReasonSchema.optional(),
    valueSummaryReason: DemographicsSummaryReasonSchema.optional(),
  })
  .refine(data => data.malePercentage + data.femalePercentage <= 100, {
    message: 'Male and female percentages combined cannot exceed 100%',
    path: ['femalePercentage'],
  })

const MediaSchema = z.object({
  thumbnailImageUrl: z.string().url({ message: 'Thumbnail image must be a valid url' }).optional(),
  landscapeImageUrl: z.string().url({ message: 'Landscape image must be a valid url' }).optional(),
  backgroundImageUrl: z.string().url({ message: 'Background image must be a valid url' }).optional(),
})

// when creating offering - we will need to associate pmp deals to a single group id
// Offering reads hydrate PMP deals and vendor names from the Deals API using the group ID.
const PrivateMarketplaceDealsSchema = z.array(
  z.object({
    ptvVendorName: z.string().min(1, 'Vendor name is required'),
    privateMarketplaceDeal: z.string().min(1, 'Partner Deal ID is required'),
    dsp: z.string().min(1, 'DSP is required'),
    ptvPublisherId: z.coerce.number().int().positive().optional(),
    liveScheduleId: IDSchema.nullable().optional(),
    privateMarketplaceDealId: IDSchema.optional(),
    // A deal with no advertiser belongs to the offering-wide default group. The
    // form expresses that as `null` (a cleared input) and a freshly seeded row
    // as `undefined`, so both spellings must pass.
    advertiserId: IDSchema.nullable().optional(),
  }),
)

/**
 * Base schema with fields shared between input and output
 */
const OfferingInputBaseSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  coverMediaUrl: z.string().nullable(),
  availabilityStartTime: RequiredCoerceDateSchema.default(() => new Date()),
  availabilityEndTime: RequiredCoerceDateSchema.nullable(),
  runStartTime: RequiredCoerceDateSchema.nullable(),
  runEndTime: RequiredCoerceDateSchema.nullable(),
  impressions: WholeNumberSchema.max(
    POSTGRES_INTEGER_MAX,
    `Impressions cannot exceed ${POSTGRES_INTEGER_MAX.toLocaleString()}`,
  ),
  flashSale: z.boolean(),
  isManagedSales: z.boolean(),
  isRequestInventory: z.boolean(),
  requestInventoryMinBudget: WholeNumberSchema.nullable().optional(),
  isPublished: z.boolean(),
  isFeatured: z.boolean().default(false),
  featuredOverline: z.string().nullable().optional(),
  featuredTagline: z.string().nullable().optional(),
  featuredImageUrl: z.string().nullable().optional(),
  campaignTemplateGroupId: z.number().min(1, 'Deal type is required'),
  salesSheet: SalesSheetSchema.nullable().optional(),
  demographics: DemographicsSchema.nullable().optional(),
})

const OfferingOutputBaseSchema = OfferingInputBaseSchema.omit({
  isRequestInventory: true,
}).extend({
  impressions: OfferingInputBaseSchema.shape.impressions.nullable(),
  requestInventoryMinBudget: WholeNumberSchema.nullable(),
  availabilityStartTime: NullableDateToIsoString,
  availabilityEndTime: NullableDateToIsoString,
  runStartTime: NullableDateToIsoString,
  runEndTime: NullableDateToIsoString,
})

/**
 * Input schema for creating/updating offerings (used by publishers)
 * Includes the original agreed CPM value
 */
const OfferingInputSchema = OfferingInputBaseSchema.extend({
  impressions: OfferingInputBaseSchema.shape.impressions.nullable().default(null),
  cpm: z.number().positive('CPM must be a positive number'),
  featuredOverline: z.string().max(30, 'Overline must be less than 30 characters').nullable().optional(),
  featuredTagline: z.string().max(60, 'Tagline must be less than 60 characters').nullable().optional(),
  privateMarketplaceDeals: PrivateMarketplaceDealsSchema,
})

const OfferingResponseBaseSchema = OfferingOutputBaseSchema.extend({
  offeringId: IDSchema,
  offeringVersionId: IDSchema,
  impressionsCap: WholeNumberSchema.nullable(),
  impressionsSold: WholeNumberSchema,
  impressionsAvailable: WholeNumberSchema.nullable(),
  createTime: DateToIsoString,
  showImpressionsScarcity: z.boolean(),
  media: MediaSchema,
  featuredImage: ImageAttachmentResponseWithCroppedUrlSchema.optional(),
  featuredMediaAttachmentUrl: z.string().nullable().optional(),
  featuredMediaAttachmentId: IDSchema.nullable().optional(),
  privateMarketplaceGroupId: z.number().nullable(),
  privateMarketplaceDeals: PrivateMarketplaceDealsSchema,
  isWishlisted: z.boolean().optional(),
  campaignTemplateGroupId: z.number(),
})

/**
 * Response schema for offerings viewed by advertisers.
 */
const OfferingResponseSchema = OfferingResponseBaseSchema.extend({
  storefrontCpm: z.number(),
  priorStorefrontCpm: z.number().optional(),
  isSale: z.boolean(),
  isInventoryRequest: z.boolean(),
  isRestricted: z.boolean(),
})

/**
 * Response schema for publisher offering writes.
 */
const PublisherOfferingResponseSchema = OfferingResponseBaseSchema.extend({
  cpm: z.number(),
  isRequestInventory: z.boolean(),
})

/**
 * Validation schema for offering updates
 * Includes impressionsSold for validation purposes
 */
const OfferingValidationSchema = OfferingInputSchema.extend({
  impressions: OfferingInputSchema.shape.impressions.removeDefault(),
  impressionsSold: WholeNumberSchema.optional(),
})

const OrderedProgramsSchema = z.object({
  programId: IDSchema,
  sortOrder: z.number().int().nonnegative(),
})

/**
 * Get
 */
const StatusSchema = z.union([z.literal('live'), z.literal('upcoming'), z.literal('ended'), z.literal('draft')])

const CoerceIDSchema = z.coerce.number().int().positive()

export const GetOfferingsQuerySchema = z
  .object({
    'store-id': QueryIDsSchema,
    /** Ordered offering_ids: sort only (must still pass other filters); see storefront merchandising RFD. */
    'pinned-id': QueryIDsSchema,
    'excluded-id': QueryIDsSchema,
    'offering-id': QueryIDsSchema,
    'advertiser-id': CoerceIDSchema.optional(),
    'offering-version-id': QueryIDsSchema,
    'network-id': QueryIDsSchema,
    'program-id': QueryIDsSchema,
    'only-active': CoerceBooleanSchema.optional(),
    'only-published': CoerceBooleanSchema.optional(),
    'only-available': CoerceBooleanSchema.optional(),
    'only-featured': CoerceBooleanSchema.optional(),
    /** Advertiser reads only: keeps deals with a PMP group open to the advertiser; `false` inverts it. */
    'has-available-deal': CoerceBooleanSchema.optional(),
    /** Advertiser reads only: `false` hides deals the advertiser or its primary vertical is restricted from. */
    'is-restricted': CoerceBooleanSchema.pipe(z.literal(false)).optional(),
    'include-purchased-managed-sales': CoerceBooleanSchema.optional(),
    'wishlist-id': CoerceIDSchema.optional(),
    statuses: z
      .union([StatusSchema, z.array(StatusSchema)])
      .transform(v => (Array.isArray(v) ? v : [v]))
      .optional(),
    'program-count': CoerceNumberSchema.pipe(NaturalNumberSchema).optional(),
    search: z.string().optional(),
    exclude: z.string().optional(),
    'run-dates-start': RequiredCoerceDateSchema.optional(),
    'run-dates-end': RequiredCoerceDateSchema.optional(),
    'min-availability-start': RequiredCoerceDateSchema.optional(),
    'max-availability-start': RequiredCoerceDateSchema.optional(),
    'min-availability-end': RequiredCoerceDateSchema.optional(),
    'max-availability-end': RequiredCoerceDateSchema.optional(),
    'cpm-min': CoerceNumberSchema.pipe(PositiveDecimalSchema).optional(),
    'cpm-max': CoerceNumberSchema.pipe(PositiveDecimalSchema).optional(),
    order: z.union([z.literal('asc'), z.literal('desc')]).optional(),
    'order-by': z
      .union([
        z.literal('created'),
        z.literal('name'),
        z.literal('search'),
        z.literal('cpm'),
        z.literal('program_count'),
        z.literal('recent_order_volume'),
        z.literal('availability_start'),
        z.literal('availability_end'),
        z.literal('run_start'),
        z.literal('impressions'),
      ])
      .optional(),
    'program-genre-id': QueryIDsSchema.optional().default([]),
    'program-rating-id': QueryIDsSchema.optional().default([]),
  })
  .extend(PaginationQuerySchema.shape)

export const OfferingListItemResponseSchema = OfferingResponseSchema.extend({
  programs: z.array(
    ProgramSchema.omit({ defaultCpm: true }).extend({
      network: z.object({
        name: z.string().min(1, 'Name is required'),
        description: z.string().min(1, 'Description is required'),
        logo: ImageAttachmentResponseWithCroppedUrlSchema,
        networkId: IDSchema,
      }),
      programRating: ProgramRatingResponseSchema,
      videoAttachments: z.array(VideoAttachmentSchema.extend({ sortOrder: z.number() })),
      artwork: ImageAttachmentResponseWithCroppedUrlSchema,
      background: ImageAttachmentResponseWithCroppedUrlSchema,
      sortOrder: z.number().int().nonnegative(),
    }),
  ),
}).omit({ privateMarketplaceDeals: true })

const GetOfferingsResponseBodySchema = z.object({
  data: z.array(
    OfferingListItemResponseSchema.extend({
      hasPurchased: z.boolean(),
      programs: z.array(
        OfferingListItemResponseSchema.shape.programs.element.extend({
          genres: z.array(ProgramGenreResponseSchema),
        }),
      ),
    }),
  ),
  page: PageResponseSchema,
})

export const GetOfferingsResponseSchema = {
  200: GetOfferingsResponseBodySchema,
  401: E('DEALS_CLIENT_AUTHENTICATION_ERROR'),
  403: z.union([
    E('OFFERING_VERSION_PRIVATE_MARKETPLACE_GROUP_ACCESS_DB_ERROR'),
    E('DEALS_CLIENT_AUTHORIZATION_ERROR'),
  ]),
  404: E('DEALS_CLIENT_NOT_FOUND_ERROR'),
  500: z.union([
    E('DEALS_CLIENT_INPUT_VALIDATION_ERROR'),
    E('DEALS_CLIENT_RESPONSE_PARSE_ERROR'),
    E('REDIS_CLIENT_CONNECT_ERROR'),
    E('REDIS_CLIENT_ERROR'),
    E('EMBEDDING_MODEL_GENERATION_ERROR'),
    E('OFFERING_NOT_FOUND_DB_ERROR'),
    E('GARY_REST_API_ERROR'),
    E('GARY_BASIC_AUTH_CREDENTIALS_ERROR'),
    E('OFFERING_VERSIONS_X_PRIVATE_MARKETPLACE_GROUPS_FETCH_DB_ERROR'),
    E('OFFERING_UNEXPECTED_FAILURE'),
    E('PURCHASE_RESTRICTION_DB_ERROR'),
    E('SHOPPER_GRAPH_CLIENT_ERROR'),
    E('SHOPPER_GRAPH_RESPONSE_PARSE_ERROR'),
    E('DEALS_CLIENT_INTERNAL_SERVER_ERROR'),
    E('DEALS_CLIENT_UNEXPECTED_RESPONSE_ERROR'),
    E('ORDER_ITEMS_BY_ADVERTISER_DB_ERROR'),
  ]),
  502: E('DEALS_CLIENT_BAD_GATEWAY_ERROR'),
  503: E('DEALS_CLIENT_UNAVAILABLE_ERROR'),
}

/**
 * Post
 */
export const PostOfferingBodySchema = z
  .object({
    offering: applyValidations(OfferingInputSchema, OFFERING_VALIDATIONS),
    orderedPrograms: z.array(OrderedProgramsSchema).min(1, 'At least 1 program is required'),
    // Use ImageAttachmentFormSchema to allow optional imageAttachmentId (consistent with updates)
    imageAttachments: z.object({ featured: ImageAttachmentFormSchema.optional() }).optional(),
    mediaAttachments: z
      .object({
        featured: z
          .object({
            url: z.url(),
            mimeType: VideoMimeTypeSchema.nullable().optional(),
            mediaAttachmentId: IDSchema.optional(),
          })
          .nullable()
          .optional(),
      })
      .optional(),
  })
  .superRefine(featuredImageAttachmentsRefinement)

export const PostOfferingResponseSchema = z.object({
  data: PublisherOfferingResponseSchema,
})

/**
 * Post Versions
 */
export const PostOfferingVersionBodySchema = z
  .object({
    offeringId: IDSchema,
    offering: applyValidations(OfferingValidationSchema.partial(), OFFERING_VERSION_VALIDATIONS),
    orderedPrograms: z.array(OrderedProgramsSchema).optional(),
    imageAttachments: z.object({ featured: ImageAttachmentFormSchemaOptionalCrop.optional() }).optional(),
    mediaAttachments: z
      .object({
        featured: z
          .object({
            url: z.url(),
            mimeType: VideoMimeTypeSchema.nullable().optional(),
            mediaAttachmentId: IDSchema.optional(),
          })
          .nullable()
          .optional(),
      })
      .optional(),
  })
  .superRefine(featuredImageAttachmentsRefinement)

export const PostOfferingVersionResponseSchema = z.object({
  data: PublisherOfferingResponseSchema,
})

/**
 * Delete
 */
export const DeleteOfferingBodySchema = z.object({
  offeringId: IDSchema,
})

export const DeleteOfferingResponseSchema = z.object({
  data: z.object({ offeringId: IDSchema }),
})

const GenerateDealInsightsProgramSchema = z.object({
  name: z.string().min(1, 'Program name is required'),
  description: z.string().optional(),
  networkName: z.string().optional(),
  ratingName: z.string().optional(),
})

// Generation accepts only prompt inputs; persisted offering version fields are validated by save endpoints.
export const GenerateDealInsightsBodySchema = z.object({
  offering: z.object({
    name: z.string().trim().min(1, 'Deal name is required'),
    description: z.string().optional(),
    impressions: WholeNumberSchema.nullable().optional(),
    cpm: z.number().positive('CPM must be a positive number').optional(),
  }),
  programs: z.array(GenerateDealInsightsProgramSchema).min(1, 'At least 1 program is required'),
  bypassCache: z.boolean().optional().default(false),
})

export const GenerateDealInsightsResponseSchema = z.object({
  data: z.object({
    demographics: DemographicsSchema,
    salesSheet: SalesSheetSchema,
    promptTemplateId: z.number(),
    cacheKey: z.string(),
  }),
})

export const GenerateAdvertiserIndustryMatchBodySchema = z.object({
  offeringId: IDSchema,
  advertiserId: IDSchema,
})

export const GenerateAdvertiserIndustryMatchResponseSchema = z.object({
  data: z.object({
    advertiserIndustry: z.string().nullable(),
    advertiserIndustryReason: z.string().nullable(),
    promptTemplateId: z.number().optional(),
    cacheKey: z.string().optional(),
  }),
})

export const toGetOfferingsDomainQuery = (query: z.output<typeof GetOfferingsQuerySchema>) => {
  const {
    'store-id': storeIds,
    'pinned-id': pinnedIds,
    'excluded-id': excludedIds,
    'offering-id': offeringIds,
    'offering-version-id': offeringVersionIds,
    'network-id': networkIds,
    'program-id': programIds,
    'wishlist-id': wishlistId,
    'only-active': onlyActive = false,
    'only-published': onlyPublished = false,
    'only-available': onlyAvailable,
    'only-featured': onlyFeatured = false,
    'include-purchased-managed-sales': includePurchasedManagedSales = false,
    'run-dates-start': runDatesStart,
    'run-dates-end': runDatesEnd,
    'min-availability-start': minAvailabilityStart,
    'max-availability-start': maxAvailabilityStart,
    'min-availability-end': minAvailabilityEnd,
    'max-availability-end': maxAvailabilityEnd,
    'cpm-min': cpmMin,
    'cpm-max': cpmMax,
    'program-genre-id': programGenreIds,
    'program-rating-id': programRatingIds,
    'program-count': programCount,
    'order-by': orderBy,
    order,
    search: searchTerm,
    exclude: exclusionTerm,
    statuses,
    limit,
    offset,
  } = query

  return {
    storeIds,
    pinnedIds: pinnedIds.length > 0 ? pinnedIds : undefined,
    excludedIds: excludedIds.length > 0 ? excludedIds : undefined,
    offeringIds,
    offeringVersionIds,
    networkIds,
    programIds,
    wishlistId,
    onlyActive,
    onlyPublished,
    onlyAvailable,
    onlyFeatured,
    includePurchasedManagedSales,
    runDatesStart,
    runDatesEnd,
    minAvailabilityStart,
    maxAvailabilityStart,
    minAvailabilityEnd,
    maxAvailabilityEnd,
    cpmMin,
    cpmMax,
    programGenreIds,
    programRatingIds,
    programCount,
    order: { order, orderBy },
    searchTerm,
    exclusionTerm,
    statuses,
    pagination: { limit, offset },
  }
}

export const GetOfferingsRequestSchema = z.union([
  z
    .object({
      query: z.custom<z.output<typeof GetOfferingsQuerySchema>>(),
      principal: T.KindSchema('advertiser').and(z.union([A.UserSchema, A.ServiceSchema])),
    })
    .transform(
      createAuthorizeAdvertiserIdTransform(
        request => request.query['advertiser-id'] ?? request.principal.tenant.current.advertiserId,
        (request, advertiserId) => ({
          in: {
            ...toGetOfferingsDomainQuery(request.query),
            advertiserId,
            hasAvailableDeal: request.query['has-available-deal'],
            isRestricted: request.query['is-restricted'],
            onlyPublished: true,
          },
          principal: request.principal,
        }),
      ),
    ),
  z
    .object({
      query: z.custom<z.output<typeof GetOfferingsQuerySchema>>(),
      principal: T.KindSchema('publisher').and(z.union([A.UserSchema, A.ServiceSchema])),
    })
    .transform((request, context) => {
      if (request.query['has-available-deal'] !== undefined || request.query['is-restricted'] !== undefined) {
        context.addIssue({
          code: 'custom',
          message: 'has-available-deal and is-restricted are relative to an advertiser',
        })
        return z.NEVER
      }
      return {
        in: toGetOfferingsDomainQuery(request.query),
        principal: request.principal,
      }
    }),
])

export const PostOfferingRequestSchema = z.object({
  body: z.custom<z.output<typeof PostOfferingBodySchema>>(),
  principal: T.KindSchema('publisher').and(z.union([A.UserSchema, A.ServiceSchema])),
})

export const PostOfferingVersionRequestSchema = z.object({
  body: z.custom<z.output<typeof PostOfferingVersionBodySchema>>(),
  principal: T.KindSchema('publisher').and(z.union([A.UserSchema, A.ServiceSchema])),
})

export const DeleteOfferingRequestSchema = z.object({
  body: z.custom<z.output<typeof DeleteOfferingBodySchema>>(),
  principal: T.KindSchema('publisher').and(z.union([A.UserSchema, A.ServiceSchema])),
})

export const GenerateAdvertiserIndustryMatchRequestSchema = z
  .object({
    body: z.custom<z.output<typeof GenerateAdvertiserIndustryMatchBodySchema>>(),
    principal: T.KindSchema('advertiser').and(A.UserSchema.and(A.HasFeaturesSchema({ advertiserIndustryMatch: true }))),
  })
  .transform(authorizeAdvertiserIdBodyTransform)

export const GenerateDealInsightsRequestSchema = z.object({
  body: z.custom<z.output<typeof GenerateDealInsightsBodySchema>>(),
  principal: T.KindSchema('publisher').and(A.UserSchema),
})

export const GenerateTextFailureResponses = {
  401: z.union([E('OPENAI_CLIENT_AUTH_ERROR'), ErrorResponseSchema]),
  500: z.union([
    E('OPENAI_CLIENT_ERROR'),
    E('OPENAI_CLIENT_RESPONSE_PARSE_ERROR'),
    E('LLM_TEXT_CACHE_PARSE_ERROR'),
    E('REDIS_CLIENT_CONNECT_ERROR'),
    E('REDIS_CLIENT_ERROR'),
    E('GET_ADVERTISERS_FAILED'),
    E('GET_ADVERTISERS_PARSE_ERROR'),
    E('SHOPPER_GRAPH_CLIENT_ERROR'),
    E('SHOPPER_GRAPH_RESPONSE_PARSE_ERROR'),
    ErrorResponseSchema,
  ]),
}

export const GenerateAdvertiserIndustryMatchFailureResponses = {
  ...GenerateTextFailureResponses,
  404: z.union([E('ADVERTISER_NOT_FOUND_ERROR'), ErrorResponseSchema]),
  502: ErrorResponseSchema,
  503: ErrorResponseSchema,
}
